import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import * as Sentry from '@sentry/nuxt';
import { defineStore } from 'pinia';

import type { TokenResponse } from '~/stores/auth/types';

import { useAddressStore } from '~/stores/address/addressStore';
import { useCartStore } from '~/stores/cart/cartStore';
import { useCustomerStore } from '~/stores/customer/customerStore';
import { useOrderStore } from '~/stores/order/orderStore';
import { useWishlistStore } from '~/stores/wishlist/wishlistStore';
import { ApiMethod, handleApiError } from '~/utils/apiClient';
import { StorageKey } from '~/utils/storage';

const STORE_ID = 'auth';

interface Actions {
  login: (username: string, password: string, context?: string) => Promise<string>;
  logout: () => Promise<void>;
}

interface Getters extends _GettersTree<State> {
  isLoggedIn: (state: State) => boolean;
}

interface State {
  accessToken: string | null;
}

export const useAuthStore = defineStore<typeof STORE_ID, State, Getters, Actions>(STORE_ID, {
  actions: {
    async login(username: string, password: string, context?: string) {
      const anonymousCartId = getItemStorage(StorageKey.CURRENT_CART);

      try {
        const response = await this.$api.fetch<TokenResponse>(ApiMethod.POST, '/auth/login', {
          anonymous_cart_id: anonymousCartId,
          context,
          password,
          username,
        });

        if (response.data) {
          this.accessToken = response.data.access_token;
          saveItemStorage(StorageKey.ACCESS_TOKEN, this.accessToken);
          removeItemStorage(StorageKey.CURRENT_CART);

          return response.data.access_token;
        }

        throw new Error('No token received');
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        throw fetchError;
      }
    },
    async logout() {
      await this.$api.fetch<TokenResponse>(ApiMethod.POST, '/auth/logout', { access_token: this.accessToken }, true);

      removeItemStorage(StorageKey.ACCESS_TOKEN);

      const addressStore = useAddressStore();
      const cartStore = useCartStore();
      const customerStore = useCustomerStore();
      const orderStore = useOrderStore();
      const wishlistStore = useWishlistStore();

      addressStore.$reset();
      cartStore.$reset();
      customerStore.$reset();
      orderStore.$reset();
      wishlistStore.$reset();
      Sentry.setUser(null);
      this.$reset();
    },
  },
  getters: {
    isLoggedIn: (state) => !!state.accessToken,
  },
  state: () => ({
    accessToken: import.meta.browser ? getItemStorage(StorageKey.ACCESS_TOKEN) : null,
  }),
});
